import { Button, Input, Select, Upload } from "antd";
import { FormikProps } from "formik";
import React from "react";

import { Components } from "~/OmnichannelApi/openapi";

export const AnswerInput: React.FC<{
  question: Components.Schemas.JobQuestion;
  formik: FormikProps<Components.Schemas.JobApplication>;
}> = ({ question, formik }) => {
  if (!question) {
    return null;
  }

  const [field] = question.fields;

  if (!field) {
    return null;
  }

  const name = String(field.name as keyof typeof formik.values);

  switch (field.type) {
    case "input_text":
      return (
        <Input
          type="text"
          name={name}
          required={question.required ? true : false}
          onChange={formik.handleChange}
          value={formik.values[name]}
        />
      );
    case "input_file": {
      const ACCEPT_MIME_TYPES = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/pdf",
        "application/rtf",
        "text/rtf",
        "text/plain",
      ];

      return (
        <Upload
          maxCount={1}
          accept={ACCEPT_MIME_TYPES.join(" ")}
          beforeUpload={(file) => {
            if (!ACCEPT_MIME_TYPES.includes(file.type)) {
              formik.setFieldError(
                name,
                `${file.type || file.name} is not an accepted file type`
              );

              return Upload.LIST_IGNORE;
            }

            if (file.size > 2 * 1024 * 1024) {
              formik.setFieldError(name, "Files must be less than 2mb");

              return Upload.LIST_IGNORE;
            }

            file.arrayBuffer().then((arrayBuffer) => {
              const blob = new Blob([new Uint8Array(arrayBuffer)], {
                type: file.type,
              });

              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = function () {
                const base64data = reader.result;

                if (base64data) {
                  formik.setFieldValue(
                    `${field.name}_content`,
                    (base64data as string).replace(
                      /^data:\w+\/\S*?;base64,/,
                      ""
                    )
                  );
                  formik.setFieldValue(
                    `${field.name}_content_filename`,
                    file.name
                  );
                }
              };
            });

            return false;
          }}
        >
          <Button type="primary">{"Upload"}</Button>
        </Upload>
      );
    }
    case "input_hidden":
      return null;
    case "textarea":
      return (
        <Input.TextArea
          name={field.name}
          onChange={formik.handleChange}
          required={question.required ? true : false}
          value={formik.values[name]}
        />
      );
    case "multi_value_single_select":
    case "multi_value_multi_select":
      return (
        <Select
          onChange={(v) => {
            formik.setFieldValue(String(field.name).replace(/\[\]$/i, ""), v);
          }}
          mode={
            field.type === "multi_value_multi_select" ? "multiple" : undefined
          }
          value={formik.values[name]}
          placeholder="Please select..."
        >
          {field.values.map(({ label, value }) => (
            <Select.Option key={value} value={value} name={label}>
              {label}
            </Select.Option>
          ))}
        </Select>
      );
    default:
      return null;
  }
};
